// --------------------------------------------------------------
// Created On: 2021-06-07
// Author: Zachary Thomas
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import styles from "./PaintQuantityModal.module.scss";

// Modal for choosing how many items get the new paint color.
export default function PaintQuantityModal(props: Props): Component {
  const [quantity, setQuantity] = useState<string | null>(null);
  const [applyToAllItems, setApplyToAllItems] = useState<boolean>(true);

  // Validation for text input field of quantity.
  function updateQuantity(quantity: string) {
    if (parseInt(quantity, 10) > 0 || quantity === "") {
      setQuantity(quantity);
    }
  }

  // Validation function prior to passing quantity state up.
  function handleApplyPaintColor(quantity: string | null, applyToAllItems: boolean) {
    if ((quantity !== null && parseInt(quantity, 10) > 0) || applyToAllItems) {
      props.onYes(quantity, applyToAllItems);
    }
  }

  return (
    <div data-test="confirmation-modal">
      <Modal
        show={props.showModal}
        onHide={() => props.onClose()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <ModalHeader>
          <h5>{props.title}</h5>
        </ModalHeader>
        <ModalBody>
          <p className={`"${styles.label} mx-2 mb-4"`}>{props.content}</p>
          <div>
            <div className="mx-2 mb-4">
              <div className="row align-items-center">
                {/* Apply to all items checkbox */}
                <div className="col-auto">
                  <input
                    className="form-check-input form-check-big"
                    type="checkbox"
                    id="apply-all-checkbox"
                    checked={applyToAllItems}
                    onChange={(e) => setApplyToAllItems(e.target.checked)}
                  />
                </div>

                {/* Apply to all items label */}
                <div className="col">
                  <label className={`${styles.label}`}>Apply to all items of this type</label>
                </div>
                {props.errorMessage && <span className={`${styles.errorMessage}`}>{props.errorMessage}</span>}
                {!applyToAllItems && (
                  <div className={`${styles.quantityInput}`}>
                    <input
                      className="form-control"
                      data-test="paint-quantity-input"
                      type="text"
                      maxLength={10}
                      aria-describedby="quantity"
                      placeholder="Enter quantity..."
                      onChange={(e) => updateQuantity(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {props.yesText !== undefined && (
            <button
              data-test="paint-quantity-modal-confirm-button"
              type="button"
              className={`btn ${props.danger ? "btn-danger" : "btn-primary"}`}
              data-dismiss="modal"
              onClick={() => handleApplyPaintColor(quantity, applyToAllItems)}
            >
              {props.yesText}
            </button>
          )}
          {props.noText !== undefined && (
            <button
              data-test="paint-quantity-modal-cancel-button"
              type="button"
              className="btn btn-secondary"
              onClick={() => props.onNo()}
            >
              {props.noText}
            </button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  title: string;
  content: string | string[];
  yesText?: string;
  noText?: string;
  danger?: boolean;
  onClose: () => void;
  onYes: (quantity: string | null, applyToAllItems: boolean) => void;
  onNo: () => void;
  errorMessage: string | null;
}
